import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Link } from "react-router-dom";

type DataSource = {
  id: string;
  name: string;
  type: string;
  status: string;
  host: string | null;
  port: number | null;
  database: string | null;
  user: string | null;
  sslMode: string | null;
  hasPassword: boolean;
  connectionStatus: string;
  lastProbeAt: string | null;
  lastProbeError: string | null;
  createdAt: string;
};

type FormData = {
  name: string;
  host: string;
  port: string;
  database: string;
  user: string;
  password: string;
  sslMode: string;
};

export function DataSourcesPage() {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingDs, setEditingDs] = useState<DataSource | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ items: DataSource[] }>({
    queryKey: ["data-sources"],
    queryFn: () => api.get("/data-sources"),
  });

  const testConnection = useMutation({
    mutationFn: (id: string) => {
      setTestingId(id);
      return api.post(`/data-sources/${id}/test`);
    },
    onSettled: () => setTestingId(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["data-sources"] }),
    onError: (err: Error) => setGlobalError(err.message),
  });

  const deleteDs = useMutation({
    mutationFn: (id: string) => {
      setDeletingId(id);
      return api.del(`/data-sources/${id}`);
    },
    onSettled: () => setDeletingId(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["data-sources"] }),
    onError: (err: Error) => setGlobalError(err.message),
  });

  function handleEdit(ds: DataSource) {
    setEditingDs(ds);
    setIsAdding(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleAddToggle() {
    setIsAdding((v) => !v);
    setEditingDs(null);
  }

  if (isLoading) return <p role="status" className="text-sm text-gray-500">Carregando conexões…</p>;

  return (
    <div className="space-y-6">
      {globalError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg flex items-start justify-between gap-2">
          <span>{globalError}</span>
          <button onClick={() => setGlobalError(null)} className="text-red-400 hover:text-red-600 shrink-0">✕</button>
        </div>
      )}

      <header className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">Bancos de Dados</p>
          <p className="text-xs text-gray-500 mt-0.5">Conexões somente leitura com o banco do ERP, de onde a sincronização busca os dados.</p>
        </div>
        <button onClick={handleAddToggle} className={isAdding ? "btn-secondary" : "btn-primary"}>
          {isAdding ? "Cancelar" : "+ Nova conexão"}
        </button>
      </header>

      {isAdding && (
        <div className="bg-white p-6 rounded-xl border border-surface-border shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-4">Adicionar Conexão PostgreSQL</h2>
          <DataSourceForm
            onSuccess={() => {
              setIsAdding(false);
              queryClient.invalidateQueries({ queryKey: ["data-sources"] });
            }}
            onCancel={() => setIsAdding(false)}
          />
        </div>
      )}

      {editingDs && (
        <div className="bg-white p-6 rounded-xl border border-surface-border shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-4">Editar: {editingDs.name}</h2>
          <DataSourceForm
            existing={editingDs}
            onSuccess={() => {
              setEditingDs(null);
              queryClient.invalidateQueries({ queryKey: ["data-sources"] });
            }}
            onCancel={() => setEditingDs(null)}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data?.items.map((ds) => (
          <div key={ds.id} className="bg-white p-5 rounded-xl border border-surface-border shadow-card flex flex-col">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">{ds.name}</h3>
                <div className="text-xs text-gray-500 mt-0.5">{ds.host}:{ds.port} / {ds.database}</div>
              </div>
              <StatusBadge status={ds.connectionStatus} />
            </div>

            <div className="flex-1 text-xs text-gray-500 space-y-1 mb-4">
              <div><span className="font-medium text-gray-700">Usuário:</span> {ds.user}</div>
              <div><span className="font-medium text-gray-700">SSL:</span> {ds.sslMode}</div>
              <div><span className="font-medium text-gray-700">Senha Salva:</span> {ds.hasPassword ? "Sim" : "Não"}</div>
              {ds.lastProbeError && (
                <div className="text-red-500 mt-2 p-2 bg-red-50 rounded border border-red-100 break-all">
                  Erro: {ds.lastProbeError}
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-surface-border flex items-center justify-between mt-auto gap-2">
              <button
                onClick={() => testConnection.mutate(ds.id)}
                disabled={testingId === ds.id}
                className="text-xs font-medium text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded bg-blue-50 hover:bg-blue-100 transition disabled:opacity-50"
              >
                {testingId === ds.id ? "Testando..." : "Testar Conexão"}
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEdit(ds)}
                  className="text-xs font-medium text-gray-700 hover:text-gray-900 px-3 py-1.5 rounded bg-surface-muted hover:bg-gray-100 transition"
                >
                  Editar
                </button>
                <Link
                  to={`/data-sources/${ds.id}/mappings`}
                  className="text-xs font-medium text-gray-700 hover:text-gray-900 px-3 py-1.5 rounded bg-surface-muted hover:bg-gray-100 transition"
                >
                  Mapeamentos
                </Link>
                <button
                  onClick={() => { if (confirm("Tem certeza que deseja remover esta conexão?")) deleteDs.mutate(ds.id); }}
                  disabled={deletingId === ds.id}
                  className="text-xs text-red-500 hover:text-red-700 px-2 py-1 disabled:opacity-50"
                >
                  {deletingId === ds.id ? "..." : "Remover"}
                </button>
              </div>
            </div>
          </div>
        ))}
        {data?.items.length === 0 && !isAdding && (
          <div className="col-span-full py-12 text-center border-2 border-dashed border-surface-border rounded-xl text-gray-500">
            Nenhuma conexão configurada.
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    ok:       { label: "Conectado",   color: "bg-green-100 text-green-700 border-green-200" },
    error:    { label: "Erro",        color: "bg-red-100 text-red-700 border-red-200" },
    untested: { label: "Não testado", color: "bg-gray-100 text-gray-600 border-gray-200" },
  };
  const cfg = map[status] ?? map["untested"]!;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium uppercase tracking-wide ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function DataSourceForm({
  existing,
  onSuccess,
  onCancel,
}: {
  existing?: DataSource;
  onSuccess: () => void;
  onCancel?: () => void;
}) {
  const isEditing = !!existing;

  const [formData, setFormData] = useState<FormData>({
    name:     existing?.name     ?? "",
    host:     existing?.host     ?? "",
    port:     String(existing?.port ?? "5432"),
    database: existing?.database ?? "",
    user:     existing?.user     ?? "",
    password: "",
    sslMode:  existing?.sslMode  ?? "disable",
  });

  const create = useMutation({
    mutationFn: (data: FormData) =>
      api.post("/data-sources", { ...data, port: parseInt(data.port, 10) }),
    onSuccess,
  });

  const update = useMutation({
    mutationFn: (data: FormData) => {
      const body: Record<string, unknown> = {
        name:     data.name,
        host:     data.host,
        port:     parseInt(data.port, 10),
        database: data.database,
        user:     data.user,
        sslMode:  data.sslMode,
      };
      // Omite password se vazio — o backend interpreta ausência como "manter atual".
      if (data.password) body.password = data.password;
      return api.put(`/data-sources/${existing!.id}`, body);
    },
    onSuccess,
  });

  const mutation = isEditing ? update : create;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  const field = (key: keyof FormData) => ({
    value: formData[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setFormData({ ...formData, [key]: e.target.value }),
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {mutation.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">
          {(mutation.error as Error).message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Nome da Conexão</label>
          <input required type="text" {...field("name")} className="w-full border border-surface-border rounded-md p-2 text-sm" placeholder="ex: Produção ERP" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Database</label>
          <input required type="text" {...field("database")} className="w-full border border-surface-border rounded-md p-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Host</label>
          <input required type="text" {...field("host")} className="w-full border border-surface-border rounded-md p-2 text-sm" placeholder="postgres.exemplo.com" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Porta</label>
          <input required type="number" {...field("port")} className="w-full border border-surface-border rounded-md p-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Usuário</label>
          <input required type="text" {...field("user")} className="w-full border border-surface-border rounded-md p-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Senha{isEditing ? " (deixe em branco para manter a atual)" : " (Opcional)"}
          </label>
          <input type="password" {...field("password")} className="w-full border border-surface-border rounded-md p-2 text-sm" placeholder={isEditing ? "Sem alteração" : "***"} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">SSL Mode</label>
          <select {...field("sslMode")} className="w-full border border-surface-border rounded-md p-2 text-sm bg-white">
            <option value="disable">Disable</option>
            <option value="require">Require</option>
            <option value="verify-ca">Verify CA</option>
            <option value="verify-full">Verify Full</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 rounded-lg text-sm font-medium border border-surface-border text-gray-700 hover:bg-surface-muted transition"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={mutation.isPending}
          className="bg-gold-500 hover:bg-gold-600 text-gray-900 px-6 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50"
        >
          {mutation.isPending ? "Salvando..." : isEditing ? "Salvar Alterações" : "Salvar Conexão"}
        </button>
      </div>
    </form>
  );
}
